import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Card, cx } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { msg, t } from "@/lib/i18n";

type Source = "none" | "shopify" | "klaviyo";
const OPTIONS: { value: Source; label: string; hint: string }[] = [
  { value: "none", label: msg("Keine"), hint: msg("Nur die Versandbestätigung, die Shopify beim Versenden schickt.") },
  { value: "shopify", label: msg("Shopify"), hint: msg("Connect meldet „unterwegs“, „in Zustellung“, „zugestellt“ und Probleme an Shopify. Shopify zeigt den Stand in der Bestellung und verschickt seine Mails im Design des Shops – welche, legt ihr in Shopify unter Benachrichtigungen fest.") },
  { value: "klaviyo", label: msg("Klaviyo"), hint: msg("Versand- und Zustellereignisse gehen an Klaviyo, eure Flows verschicken die Mails. An Shopify gehen keine Zustellstufen – so entstehen keine doppelten Mails.") },
];

/**
 * Wer verschickt Versand- und Zustell-Mails an Endkunden? Nur Logiful-Admins stellen um (Mails an Endkunden
 * erst nach Freigabe durch Logiful); alle anderen sehen die Einstellung.
 */
export function DeliveryNotificationsCard({ channelId, shop, value, klaviyoConnected }: { channelId: string; shop: string; value: Source; klaviyoConnected: boolean }) {
  const { tenantId, me } = useTenant();
  const qc = useQueryClient();
  const canEdit = me.user.isStaff && me.user.staffRole === "staff_admin";
  const save = useMutation({
    mutationFn: (source: Source) => api.channels.setDeliveryNotifications(tenantId, channelId, source),
    onSuccess: () => {
      toast.success(t("Gespeichert"));
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: (e) => toast.error(t("Nicht gespeichert"), { description: e instanceof ApiError ? (e.body as { message?: string })?.message : undefined }),
  });
  const notifications = `https://admin.shopify.com/store/${shop.replace(".myshopify.com", "")}/settings/notifications`;

  return (
    <Card className="p-5">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><Mail className="size-4 text-fg-muted" /> {t("Versand- und Zustell-Mails an Endkunden")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">
        {t("Wer informiert eure Kunden, wenn das Paket unterwegs, in Zustellung oder zugestellt ist?")}
        {!canEdit && <span className="inline-flex items-center gap-1 ml-1 text-fg-subtle"><Lock className="size-3" /> {t("Umstellung durch Logiful.")}</span>}
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={t("Versand- und Zustell-Mails an Endkunden")}>
        {OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            disabled={!canEdit || save.isPending}
            onClick={() => value !== o.value && save.mutate(o.value)}
            className={cx("text-left rounded-xl border p-3 transition-colors disabled:cursor-default", value === o.value ? "border-ink bg-ink/[0.04]" : "border-border hover:bg-surface-2/60 disabled:hover:bg-transparent", !canEdit && value !== o.value && "opacity-60")}
          >
            <span className="block text-[13px] font-medium">{t(o.label)}</span>
            <span className="block mt-0.5 text-[12px] text-fg-muted">{t(o.hint)}</span>
          </button>
        ))}
      </div>
      {value === "shopify" && klaviyoConnected && (
        <p className="mt-3 flex items-start gap-2 text-[12.5px] text-warning"><AlertTriangle className="size-4 shrink-0 mt-px" /> {t("Klaviyo ist ebenfalls verbunden. Verschicken eure Klaviyo-Flows auch Zustell-Mails, bekommen Kunden sie doppelt.")}</p>
      )}
      {value === "klaviyo" && !klaviyoConnected && (
        <p className="mt-3 flex items-start gap-2 text-[12.5px] text-warning"><AlertTriangle className="size-4 shrink-0 mt-px" /> {t("Klaviyo ist noch nicht verbunden (Einstellungen → Tool-Anbindungen) – bis dahin gehen keine Zustell-Mails raus.")}</p>
      )}
      {value !== "shopify" && (
        <p className="mt-3 text-[12px] text-fg-subtle">{t("Shopify verschickt eigene Mails „In Zustellung“/„Zugestellt“ nur, wenn Connect Zustellstufen meldet – bei dieser Einstellung also nicht.")}</p>
      )}
      {value === "shopify" && (
        <a href={notifications} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg underline-offset-2 hover:underline"><ExternalLink className="size-3.5" /> {t("Benachrichtigungen in Shopify")}</a>
      )}
    </Card>
  );
}
